import { Phone, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  return (
    <section 
      className="relative min-h-screen flex items-center justify-center pt-20"
      data-testid="hero-section"
    >
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1511207538754-e8555f2bc187?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxkYXJrJTIwZ3JlZW4lMjBmb3Jlc3R8ZW58MHx8fHwxNzc0NTkyNTk0fDA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
        <div className="max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-[#2A4B35]/30 backdrop-blur-sm border border-[#2A4B35]/50 rounded-full px-4 py-2 mb-8">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            <span className="text-[#F5F3ED] text-sm font-medium">Available 24/7 in Bangalore</span>
          </div>

          {/* Main Heading - H1 */}
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="hero-heading"
          >
            Snake Rescue Bangalore
            <span className="block text-[#25D366] text-3xl sm:text-4xl lg:text-5xl mt-2">24/7 Snake Catcher Near Me</span>
          </h1>

          {/* Subheading */}
          <p className="text-lg md:text-xl text-[#E8E6E0] mb-10 max-w-2xl mx-auto" data-testid="hero-subheading">
            Found a <strong>snake in house</strong>? Don't panic, call the expert. Professional <strong>snake removal Bangalore</strong> service with 
            safe, humane rescue and relocation. <strong>Snake catcher number</strong>: +91 8310755854
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col items-center gap-4">
            {/* Primary Emergency Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="tel:+918310755854" data-testid="hero-call-btn-alhan">
                <Button 
                  size="lg"
                  className="bg-[#D9534F] hover:bg-[#c9433f] text-white font-bold text-base md:text-lg px-6 md:px-8 py-5 md:py-6 rounded-full emergency-pulse flex items-center gap-3 shadow-2xl"
                >
                  <Phone className="w-5 h-5 md:w-6 md:h-6" />
                  <span className="flex flex-col items-start">
                    <span className="text-xs opacity-80">Alhan Nawaz</span>
                    <span>+91 8310755854</span>
                  </span>
                </Button>
              </a>

              <a href="tel:+919845080903" data-testid="hero-call-btn-mohan">
                <Button 
                  size="lg"
                  className="bg-[#D9534F] hover:bg-[#c9433f] text-white font-bold text-base md:text-lg px-6 md:px-8 py-5 md:py-6 rounded-full emergency-pulse flex items-center gap-3 shadow-2xl"
                >
                  <Phone className="w-5 h-5 md:w-6 md:h-6" />
                  <span className="flex flex-col items-start">
                    <span className="text-xs opacity-80">Mohan K</span>
                    <span>+91 9845080903</span>
                  </span>
                </Button>
              </a>
            </div>

            {/* WhatsApp Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a 
                href="https://wa.me/918310755854?text=Hi%2C%20I%20need%20snake%20rescue%20help!"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn-alhan"
              >
                <Button 
                  size="lg"
                  className="bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold px-6 py-4 rounded-full whatsapp-btn flex items-center gap-2"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp Alhan
                </Button>
              </a>

              <a 
                href="https://wa.me/919845080903?text=Hi%2C%20I%20need%20snake%20rescue%20help!"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn-mohan"
              >
                <Button 
                  size="lg"
                  className="bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold px-6 py-4 rounded-full whatsapp-btn flex items-center gap-2"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp Mohan
                </Button>
              </a>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { number: '10,000+', label: 'Rescues Completed' },
              { number: '24/7', label: 'Emergency Service' },
              { number: '10+', label: 'Years Experience' },
              { number: '100%', label: 'Safe Release' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {stat.number}
                </div>
                <div className="text-[#B8B5AC] text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-white/60 rounded-full animate-bounce"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
